import React, { useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { useContentManagement } from 'api/portal/useContentManagement';
import { z } from 'zod';

import Input from 'components/Input/Input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'components/ui/form';

import {
  ContentSelectionMode,
  EContentType,
  ERROR_FIELD_CLASS,
  IBlockContentProps
} from '../../../constants';
import {
  contentsPreviewBlockSchema,
  pageFormSchema
} from '../../../validation-schema';
import { BlockSegmentsField } from '../Fields/BlockSegmentsField';
import { BlockVisibilityField } from '../Fields/BlockVisibilityField';

import {
  ContentSelectionSection,
  ContentTypeSelect
} from './ContentsPreview';

type ContentsPreviewBlockType = z.infer<typeof contentsPreviewBlockSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

type ContentsPreviewFieldProps<T extends keyof ContentsPreviewBlockType> =
  import('react-hook-form').ControllerRenderProps<PageFormType, any> & {
    value: ContentsPreviewBlockType[T];
  };

export const ContentsPreviewForm: React.FC<IBlockContentProps> = ({
  block,
  blockIndex
}) => {
  const { control, watch, getFieldState, trigger } =
    useFormContext<z.infer<typeof pageFormSchema>>();
  const { getCreatedContentsData } = useContentManagement();

  const basePath = `blocksV2.${blockIndex}` as const;
  const currentContentType =
    (watch(`${basePath}.contentType`) as EContentType) || EContentType.NEWS;
  const subType =
    watch(`${basePath}.subType`) ??
    ContentSelectionMode.LAST_CONTENTS;
  const selectedContentsIds = watch(`${basePath}.selectedContentsIds`) ?? [];

  const getFieldError = (fieldName: string) =>
    getFieldState(fieldName as any).error;

  const contentItemsOptions = useMemo(() => {
    const contentsData = getCreatedContentsData?.data?.contents ?? [];
    return contentsData
      .filter(
        (content: { contentType?: string }) =>
          content.contentType === currentContentType
      )
      .sort(
        (a: { createdAt?: string }, b: { createdAt?: string }) =>
          new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
      )
      .map((content: { id: string; title: string }) => ({
        value: content.id,
        label: content.title || content.id
      }));
  }, [getCreatedContentsData?.data?.contents, currentContentType]);

  return (
    <div className="flex flex-col gap-4 p-4 rounded-md border transition-colors border-base-border">
      <ContentTypeSelect
        blockIndex={blockIndex}
        currentContentType={currentContentType}
        subType={subType}
        selectedContentsIds={selectedContentsIds}
      />

      <FormField
        control={control}
        name={`${basePath}.title`}
        render={({ field }: { field: ContentsPreviewFieldProps<'title'> }) => (
          <FormItem>
            <FormLabel>* Title</FormLabel>
            <FormControl>
              <Input
                isRequired
                value={field.value}
                onChange={field.onChange}
                onBlur={() => {
                  field.onBlur();
                  trigger('blocksV2');
                }}
                placeholder="Enter title"
                className={
                  getFieldError(`${basePath}.title`)?.message
                    ? ERROR_FIELD_CLASS
                    : ''
                }
                state={
                  getFieldError(`${basePath}.title`)?.message
                    ? 'error'
                    : 'default'
                }
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />

      <ContentSelectionSection
        blockIndex={blockIndex}
        subType={subType}
        contentItemsOptions={contentItemsOptions}
        getFieldError={getFieldError}
      />

      <BlockVisibilityField basePath={basePath} />
      <BlockSegmentsField basePath={basePath} />
    </div>
  );
};
